import { useState, useEffect } from "react";
import axios from "axios";
import { Search, MapPin, Phone, Mail, Calendar, Clock, CheckCircle, AlertCircle, XCircle } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PIN = "epsom2026";

const STATUS_COLORS = {
  PAYMENT_PENDING: "bg-amber-900/30 text-amber-300 border-amber-700/50",
  PAYMENT_VERIFIED: "bg-emerald-900/30 text-emerald-300 border-emerald-700/50",
  CONFIRMED: "bg-blue-900/30 text-blue-300 border-blue-700/50",
  COMPLETED: "bg-stone-800/30 text-stone-400 border-stone-700/50",
  CANCELLED: "bg-red-900/30 text-red-300 border-red-700/50",
};
const STATUS_ICONS = { PAYMENT_PENDING: AlertCircle, PAYMENT_VERIFIED: CheckCircle, CONFIRMED: CheckCircle, COMPLETED: CheckCircle, CANCELLED: XCircle };

export default function AdminPanel() {
  const [authenticated, setAuthenticated] = useState(false);
  const [pin, setPin] = useState("");
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const login = () => { if (pin === ADMIN_PIN) { setAuthenticated(true); toast.success("Welcome to Epsom Admin"); } else { toast.error("Incorrect PIN"); } };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter) params.set("status", statusFilter);
      if (locationFilter) params.set("location", locationFilter);
      const res = await axios.get(`${API}/bookings?${params.toString()}`);
      setBookings(res.data);
    } catch { toast.error("Failed to load bookings"); }
    setLoading(false);
  };

  const fetchStats = async () => { try { const res = await axios.get(`${API}/bookings/stats`); setStats(res.data); } catch (e) { console.error(e); } };

  const updateStatus = async (id, newStatus) => {
    try { await axios.put(`${API}/bookings/${id}/status?status=${newStatus}`); toast.success(`Status updated`); fetchBookings(); fetchStats(); }
    catch { toast.error("Failed to update"); }
  };

  useEffect(() => { if (authenticated) { fetchBookings(); fetchStats(); } }, [authenticated, statusFilter, locationFilter]); // eslint-disable-line
  useEffect(() => { if (!authenticated) return; const t = setTimeout(fetchBookings, 400); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  if (!authenticated) {
    return (
      <div data-testid={TEST_IDS.adminPanel} className="min-h-screen bg-[#0d0b09] flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-[#17120e] border border-[rgba(242,207,139,0.18)] rounded-2xl p-8 shadow-[0_24px_80px_rgba(0,0,0,0.45)] text-center">
          <img src="/assets/epsom-logo.webp" alt="Epsom" className="w-24 h-auto mx-auto mb-6 brightness-0 invert opacity-80" />
          <h2 className="font-serif text-2xl font-semibold text-[#f8f0df] mb-2">Admin Access</h2>
          <p className="text-sm text-[#bdb3a4] mb-6">Enter PIN to manage bookings</p>
          <input data-testid={TEST_IDS.adminLoginInput} type="password" value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Enter admin PIN" className="w-full px-4 py-3 rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white placeholder:text-[#665846] focus:outline-none focus:border-[#d7a85c] mb-4" />
          <button data-testid={TEST_IDS.adminLoginBtn} onClick={login}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-[#f1cb82] to-[#b77a31] text-[#1b1108] font-bold hover:shadow-xl transition-all">Enter Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={TEST_IDS.adminPanel} className="min-h-screen bg-[#0d0b09]">
      <header className="sticky top-0 z-20 bg-[#0d0b09]/95 backdrop-blur-md border-b border-[rgba(242,207,139,0.18)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/epsom-logo.webp" alt="Epsom" className="h-8 brightness-0 invert opacity-80" />
            <span className="text-sm font-semibold text-[#bdb3a4]">Admin Panel</span>
          </div>
          <button onClick={() => setAuthenticated(false)} className="text-sm text-[#bdb3a4] hover:text-[#f2cf8b] transition-colors">Logout</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total", value: stats.total, color: "text-[#f8f0df]" },
              { label: "Pending", value: stats.pending, color: "text-amber-400" },
              { label: "Verified", value: stats.verified, color: "text-emerald-400" },
              { label: "Confirmed", value: stats.confirmed, color: "text-blue-400" },
            ].map((s) => (
              <div key={s.label} className="bg-[#17120e] border border-[rgba(242,207,139,0.18)] rounded-xl p-4">
                <p className="text-xs text-[#bdb3a4] mb-1">{s.label} Bookings</p>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#665846]" />
            <input data-testid={TEST_IDS.adminSearchInput} type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, email, ref..." className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-white placeholder:text-[#665846] focus:outline-none focus:border-[#d7a85c] text-sm" />
          </div>
          <select data-testid={TEST_IDS.adminStatusFilter} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-[#bdb3a4] text-sm focus:outline-none focus:border-[#d7a85c]">
            <option value="">All Statuses</option>
            <option value="PAYMENT_PENDING">Pending</option><option value="PAYMENT_VERIFIED">Verified</option>
            <option value="CONFIRMED">Confirmed</option><option value="COMPLETED">Completed</option><option value="CANCELLED">Cancelled</option>
          </select>
          <select data-testid={TEST_IDS.adminLocationFilter} value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}
            className="px-4 py-2.5 rounded-xl border border-[#3a3026] bg-[#0e0c0a] text-[#bdb3a4] text-sm focus:outline-none focus:border-[#d7a85c]">
            <option value="">All Locations</option>
            <option value="Andheri West">Andheri West</option><option value="Santacruz West">Santacruz West</option><option value="Borivali West">Borivali West</option>
          </select>
        </div>

        <div data-testid={TEST_IDS.adminBookingsTable} className="bg-[#17120e] border border-[rgba(242,207,139,0.18)] rounded-2xl overflow-hidden">
          {loading ? <div className="p-12 text-center text-[#bdb3a4]">Loading...</div> : bookings.length === 0 ? <div className="p-12 text-center text-[#bdb3a4]">No bookings found</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgba(242,207,139,0.18)] bg-[#211a14]">
                    {["Reference","Name","Contact","Location","Date & Time","UPI Ref","Status","Action"].map(h=>(
                      <th key={h} className="text-left px-4 py-3 font-semibold text-[#d5c9b8] text-xs uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const Icon = STATUS_ICONS[b.status] || AlertCircle;
                    return (
                      <tr key={b.id} className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[#211a14]/50 transition-colors">
                        <td className="px-4 py-3 font-mono text-xs text-[#f2cf8b] font-bold">{b.reference}</td>
                        <td className="px-4 py-3"><span className="font-medium text-[#f8f0df]">{b.name}</span>{b.occasion && <span className="block text-xs text-[#9f968a] mt-0.5">{b.occasion}</span>}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-[#ded4c4] text-xs"><Phone className="w-3 h-3" />{b.phone}</div>
                          <div className="flex items-center gap-1 text-[#9f968a] text-xs mt-0.5"><Mail className="w-3 h-3" />{b.email}</div>
                        </td>
                        <td className="px-4 py-3"><div className="flex items-center gap-1 text-[#ded4c4] text-xs"><MapPin className="w-3 h-3 text-[#d7a85c]" />{b.location}</div></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-[#ded4c4] text-xs"><Calendar className="w-3 h-3" />{b.preferred_date}</div>
                          <div className="flex items-center gap-1 text-[#9f968a] text-xs mt-0.5"><Clock className="w-3 h-3" />{b.preferred_time}</div>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#bdb3a4]">{b.upi_transaction_id}</td>
                        <td className="px-4 py-3">
                          <span data-testid={TEST_IDS.bookingStatusBadge} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[b.status]||""}`}>
                            <Icon className="w-3 h-3" />{b.status.replace(/_/g," ")}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <select value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)}
                            className="text-xs px-2 py-1.5 rounded-lg border border-[#3a3026] bg-[#0e0c0a] text-[#bdb3a4] focus:outline-none focus:border-[#d7a85c]">
                            <option value="PAYMENT_PENDING">Pending</option><option value="PAYMENT_VERIFIED">Verified</option>
                            <option value="CONFIRMED">Confirmed</option><option value="COMPLETED">Completed</option><option value="CANCELLED">Cancelled</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
